class IndexAnimatedTechnology extends HTMLElement {
  constructor() {
    super();
    this.currentTab = 1;
    this.isPlaying = true;
    this.isInitialized = false;
    this.isManuallyPaused = false;
    this.progressAnimationId = null;
    this.previousHumidity = null; // Track previous humidity for animation direction

    // DOM elements
    this.videos = this.querySelectorAll('.index-animated-technology__video');
    this.progressBar = this.querySelector('[data-progress]');
    this.pauseBtns = this.querySelectorAll('[data-pause-btn]');
    this.tabIndicator = this.querySelector('[data-indicator]');
    this.tabs = this.querySelectorAll('.index-animated-technology__tab');
    this.tabContents = this.querySelectorAll('.index-animated-technology__tab-content');
    this.humidityDisplay = this.querySelector('[data-humidity]');
    this.infographic = this.querySelector('.index-animated-technology__infographic');

    // Settings from Liquid
    this.tab1Humidity = parseInt(this.dataset.tab1Humidity) || 80;
    this.tab2Humidity = parseInt(this.dataset.tab2Humidity) || 60;

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupIntersectionObserver();
    this.preloadVideos();

    // Set initial chart state for tab 1
    this.updateChartForTab(this.currentTab);

    // Set initial tab content visibility
    this.setInitialTabContent();
  }

  setInitialTabContent() {
    this.tabContents.forEach(content => {
      const contentNumber = parseInt(content.dataset.content);
      if (contentNumber === this.currentTab) {
        content.style.opacity = '1';
      } else {
        content.style.opacity = '0';
      }
    });
  }

  setupEventListeners() {
    // Pause buttons
    this.pauseBtns.forEach(pauseBtn => {
      pauseBtn.addEventListener('click', () => this.togglePlayPause());
    });

    // Video events
    this.videos.forEach(video => {
      video.addEventListener('ended', () => this.switchTab());
      video.addEventListener('error', (e) => this.handleVideoError(e));
      video.addEventListener('play', () => {
        this.handleVideoPlay(video);
      });
      video.addEventListener('pause', () => {
        this.handleVideoPause(video);
      });
    });

    // Manual tab switching (optional enhancement)
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabNumber = parseInt(tab.dataset.tab);
        if (tabNumber !== this.currentTab) {
          this.switchToTab(tabNumber);
        }
      });
    });

    // Resize handling
    window.addEventListener('resize', () => this.handleResize());
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isInitialized) {
          this.isInitialized = true;
          this.startVideoLoop();
        } else if (!entry.isIntersecting && this.isInitialized) {
          this.pauseAllVideos();
          this.isInitialized = false;
        }
      });
    }, { threshold: 0.3 });

    observer.observe(this);
  }

  preloadVideos() {
    this.videos.forEach(video => {
      if (video.readyState < 2) {
        video.load();
      }
    });
  }

  handleVideoError(e) {
    console.error('Video loading error:', e);
  }

  handleVideoPlay(video) {
    // Only add active class if this is the current video
    const currentVideo = this.getCurrentVideo();
    if (video === currentVideo) {
      video.classList.add('active');
    }
    this.startProgressAnimation();
  }

  handleVideoPause(video) {
    // Only remove active class if not manually paused
    if (!this.isManuallyPaused) {
      video.classList.remove('active');
    }
    this.stopProgressAnimation();
  }

  updateChartForTab(tabNumber) {
    // Update chart line visibility/opacity based on current tab
    // This creates different chart patterns for different humidity levels
    const isTab1 = tabNumber === 1;
    const targetHumidity = isTab1 ? this.tab1Humidity : this.tab2Humidity;

    // Calculate how many lines should be filled (from bottom to top)
    const totalLines = 24;
    const filledLines = Math.round((targetHumidity / 100) * totalLines);

    // Determine animation direction based on humidity change
    const isIncreasing = this.previousHumidity === null || targetHumidity > this.previousHumidity;

    // Get all chart lines (assuming they have a class like 'chart-line' or similar)
    const chartLines = this.infographic.querySelectorAll('.chart-line, .index-animated-technology__chart-line, [data-chart-line]');

    if (chartLines.length > 0) {
      // Convert NodeList to Array and reverse to start from bottom
      const linesArray = Array.from(chartLines).reverse();

      linesArray.forEach((line, index) => {
        // Calculate animation delay based on direction
        let delay;
        if (isIncreasing) {
          // Bottom to top: lower index = earlier animation
          delay = index * 0.05;
        } else {
          // Top to bottom: higher index = earlier animation
          delay = (totalLines - 1 - index) * 0.05;
        }

        line.style.setProperty('--animation-delay', `${delay}s`);

        if (index < filledLines) {
          // Line should be filled (green with full opacity)
          line.classList.add('filled');
          line.classList.remove('unfilled');
        } else {
          // Line should be unfilled (reduced opacity)
          line.classList.add('unfilled');
          line.classList.remove('filled');
        }
      });
    }

    // Update previous humidity for next comparison
    this.previousHumidity = targetHumidity;
  }

  startVideoLoop() {
    if (!this.isInitialized) return;

    const activeVideo = this.getCurrentVideo();
    if (activeVideo && activeVideo.readyState >= 2) {
      this.isManuallyPaused = false; // Reset manual pause flag
      activeVideo.currentTime = 0;
      this.progressBar.style.width = '0%'; // Reset progress bar

      // Ensure active class is added before playing
      activeVideo.classList.add('active');

      if (this.isPlaying) {
        activeVideo.play().catch(error => {
          console.error('Video play error:', error);
          // Fallback: continue with static animation
        });
      }
    }
  }

  getCurrentVideo() {
    return this.querySelector(`[data-tab="${this.currentTab}"]`);
  }

  switchTab() {
    if (!this.isPlaying || !this.isInitialized) return;

    this.switchToTab(this.currentTab === 1 ? 2 : 1);
  }

  switchToTab(tabNumber) {
    if (tabNumber === this.currentTab) return;

    // Pause current video and remove active class
    this.pauseCurrentVideo();

    // Update current tab
    this.currentTab = tabNumber;

    // Animate transition
    this.animateTabTransition();

    // Start new video
    setTimeout(() => {
      this.startNewVideo();
    }, 50); // Very short delay for seamless transition
  }

  pauseCurrentVideo() {
    const currentVideo = this.getCurrentVideo();
    if (currentVideo && !currentVideo.paused) {
      this.isManuallyPaused = false; // Reset manual pause flag
      currentVideo.pause();
      // Note: active class will be removed by handleVideoPause event
    }
  }

  pauseAllVideos() {
    this.isManuallyPaused = false; // Reset manual pause flag
    this.videos.forEach(video => {
      if (!video.paused) {
        video.pause();
        // Note: active class will be removed by handleVideoPause event
      }
    });
  }

  startNewVideo() {
    if (!this.isInitialized) return;

    const newVideo = this.getCurrentVideo();
    if (newVideo) {
      this.isManuallyPaused = false; // Reset manual pause flag
      newVideo.currentTime = 0;
      newVideo.classList.add('active');

      if (this.isPlaying) {
        newVideo.play().catch(error => {
          console.error('Video play error:', error);
        });
      }
    }
  }

  animateTabTransition() {
    // Update tab indicator with animation
    this.tabIndicator?.classList.toggle('tab-2', this.currentTab === 2);

    // Update tab states
    this.tabs.forEach(tab => {
      const tabNumber = parseInt(tab.dataset.tab);
      tab.classList.toggle('inactive', tabNumber !== this.currentTab);
    });

    // Update content with fade transition
    this.tabContents.forEach(content => {
      const contentNumber = parseInt(content.dataset.content);
      if (contentNumber === this.currentTab) {
        content.style.opacity = '1';
      } else {
        content.style.opacity = '0';
      }
    });

    // Animate humidity display
    this.animateHumidityChange();

    // Update chart for new tab
    this.updateChartForTab(this.currentTab);
  }

  animateHumidityChange() {
    const targetHumidity = this.currentTab === 1 ? this.tab1Humidity : this.tab2Humidity;
    const currentHumidity = parseInt(this.humidityDisplay.textContent);

    const duration = 800; // 0.8 seconds
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const currentValue = Math.round(currentHumidity + (targetHumidity - currentHumidity) * easeProgress);
      this.humidityDisplay.textContent = currentValue;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  startProgressAnimation() {
    this.stopProgressAnimation(); // Clear any existing animation

    const animate = () => {
      const currentVideo = this.getCurrentVideo();
      if (currentVideo && currentVideo.duration && !currentVideo.paused) {
        const progress = (currentVideo.currentTime / currentVideo.duration) * 100;
        this.progressBar.style.width = `${progress}%`;

        // Continue animation if video is still playing
        if (!currentVideo.paused && !currentVideo.ended) {
          this.progressAnimationId = requestAnimationFrame(animate);
        }
      }
    };

    this.progressAnimationId = requestAnimationFrame(animate);
  }

  stopProgressAnimation() {
    if (this.progressAnimationId) {
      cancelAnimationFrame(this.progressAnimationId);
      this.progressAnimationId = null;
    }
  }

  togglePlayPause() {
    const currentVideo = this.getCurrentVideo();
    if (!currentVideo) return;

    if (this.isPlaying) {
      // Pause the video
      this.isManuallyPaused = true;
      currentVideo.pause();
      // Note: active class will NOT be removed because isManuallyPaused = true

      this.pauseBtns.forEach(pauseBtn => {
        pauseBtn.classList.add('playing');
      });
      this.isPlaying = false;
    } else {
      // Play the video
      this.isManuallyPaused = false;
      currentVideo.classList.add('active'); // Ensure active class is added
      currentVideo.play().catch(error => {
        console.error('Video play error:', error);
      });

      this.pauseBtns.forEach(pauseBtn => {
        pauseBtn.classList.remove('playing');
      });
      this.isPlaying = true;
    }
  }

  handleResize() {
    // Chart lines are now in HTML/CSS, no need to regenerate
    // Just trigger any responsive updates if needed
    this.updateChartForTab(this.currentTab);
  }

  // Public methods for external control
  play() {
    if (!this.isPlaying) {
      this.togglePlayPause();
    }
  }

  pause() {
    if (this.isPlaying) {
      this.togglePlayPause();
    }
  }

  goToTab(tabNumber) {
    if (tabNumber === 1 || tabNumber === 2) {
      this.switchToTab(tabNumber);
    }
  }

  // Clean up on disconnect
  disconnectedCallback() {
    this.stopProgressAnimation();

    this.videos.forEach(video => {
      video.pause();
      video.removeEventListener('ended', () => this.switchTab());
      video.removeEventListener('play', () => this.handleVideoPlay(video));
      video.removeEventListener('pause', () => this.handleVideoPause(video));
    });
  }
}

// Register the custom element
customElements.define('index-animated-technology', IndexAnimatedTechnology);

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.index-animated-technology');
  sections.forEach(section => {
    if (!section.classList.contains('initialized')) {
      section.classList.add('initialized');
      // The custom element will auto-initialize
    }
  });
});
